import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { Download, ChevronDown, ChevronRight, Edit2, Trash2, Upload, Archive, RotateCcw } from "react-feather";
import Spinner from 'react-bootstrap/Spinner';
import { Table, Form, OverlayTrigger, Tooltip, Offcanvas, Button } from "react-bootstrap";

import NotyfContext from "../contexts/NotyfContext";
import { UserSettingsContext } from "../contexts/UserSettingsContext";

import getRawDocumentCategories from "./documentCategories";
import authenticatedFetch from "./fetch"
import EditableExpires from "./EditableExpires";



const ATTENTION_DAYS = 90;
const OUTDATED_DAYS = 30;
const ONE_DAY = 24 * 60 * 60 * 1000;

const CATEGORY_PRIORITY = [
  "BOARD_CERTIFICATION",
  "DEA",
  "MALPRACTICE_INSURANCE",
  "LICENSE"
];

// Uploads get a trailing epoch-millis suffix (e.g. "H3 Info-1791568334490.png"); hide it from the display name
function displayFilename(filename) {
  if (!filename) return "";
  return filename.replace(/-\d{13}(?=\.[^./]+$|$)/, "");
}

// Days from today until the date string (YYYY-MM-DD); null if blank/invalid. Negative = already expired
function daysUntil(dateString) {
  if (!dateString) return null;
  const parts = dateString.split("-");
  if (parts.length !== 3 || parseInt(parts[0]) <= 1900) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const date = new Date(parts[0], parts[1] - 1, parts[2]);  // Avoid the UTC->local timezone translation issue with js
  return Math.round((date - today) / ONE_DAY);
}

function formatDate(dateString) {
  if (!dateString) return "";
  const parts = dateString.split("-");
  if (parts.length !== 3 || parseInt(parts[0]) <= 1900) return dateString;
  return new Date(parts[0], parts[1] - 1, parts[2]).toLocaleDateString("en-US");
}

const ChevronsIcon = ({ points }) => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={points} />
  </svg>
);

const IconAction = ({ label, onClick, children }) => (
  <OverlayTrigger placement="top" overlay={<Tooltip>{label}</Tooltip>}>
    <span role="button" tabIndex={0} aria-label={label} style={{display: "inline-flex", cursor: "pointer"}} onClick={onClick} onKeyDown={(e) => { if (e.key === "Enter") onClick(e); }}>
      {children}
    </span>
  </OverlayTrigger>
);

// Archived, or expired long enough ago to be hidden by default
function isOutdated(doc) {
  if (doc.archived) return true;
  const days = daysUntil(doc.expires);
  return days !== null && -days > OUTDATED_DAYS;
}

const thStyle = { fontWeight: 500, borderBottom: "1px solid #dee2e6" };

const softPill = (color, background) => ({ color: color, backgroundColor: background, fontWeight: 500, fontSize: "0.75em", borderRadius: "10px", padding: "2px 8px", whiteSpace: "nowrap" });

function ExpiryStatus({ expires, archived }) {
  const days = daysUntil(expires);
  if (archived) {
    return (
      <span className="d-inline-flex flex-wrap align-items-center" style={{columnGap: "0.5rem"}}>
        <span>{expires ? formatDate(expires) : <span className="text-muted">&mdash;</span>}</span>
        <span style={softPill("#495057", "#e9ecef")}>Archived</span>
      </span>
    );
  }
  if (days === null) {
    return <span className="text-muted">&mdash;</span>;
  }

  let pill = null;
  if (days < 0) {
    pill = <span style={softPill("#b02a37", "#fbe3e5")}>Expired</span>;
  } else if (days <= OUTDATED_DAYS) {
    pill = <span style={softPill("#b02a37", "#fbe3e5")}>{days === 0 ? "Today" : days + "d left"}</span>;
  } else if (days <= ATTENTION_DAYS) {
    pill = <span style={softPill("#8a5a00", "#fff1d0")}>{days}d left</span>;
  }

  return (
    <span className="d-inline-flex flex-wrap align-items-center" style={{columnGap: "0.5rem"}}>
      <span>{formatDate(expires)}</span>
      {pill}
    </span>
  );
}

function buildApi(entityType, entityId, isH3AdminFile, isRevenueTeamSpanningFile, isPracticeSpecificFile, practiceId) {
  const prefix = isH3AdminFile ? "h3admin/" : (isRevenueTeamSpanningFile ? "revenueteamspanning/" : (isPracticeSpecificFile ? "practicespecific/" : ""));
  return {
    path: (suffix) => entityType + "/" + prefix + entityId + "/" + suffix,
    headers: isPracticeSpecificFile ? { practiceId: practiceId } : {},
  };
}

const DocumentEditDrawer = ({ doc, allFilenames, api, showNotes, rawDocumentCategories, onClose, onSaved }) => {
  const notyf = useContext(NotyfContext);
  const { documentsRefresh, setDocumentsRefresh } = useContext(UserSettingsContext);

  const filenameRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [filename, setFilename] = useState(doc ? doc.filename : "");
  const [category, setCategory] = useState(doc ? (doc.category || "") : "");
  const [effectiveDate, setEffectiveDate] = useState(doc ? doc.effectiveDate : null);
  const [expires, setExpires] = useState(doc ? doc.expires : null);
  const [notes, setNotes] = useState(doc ? (doc.notes || "") : "");
  const [archived, setArchived] = useState(doc ? doc.archived === true : false);

  useEffect(() => {
    if (doc) {
      setFilename(doc.filename);
      setCategory(doc.category || "");
      setEffectiveDate(doc.effectiveDate);
      setExpires(doc.expires);
      setNotes(doc.notes || "");
      setArchived(doc.archived === true);
    }
  }, [doc]);

  // If they clear it out, date picker sends it back as "", not null
  const blankToNull = (date) => (date === "" ? null : date);

  function save(e) {
    e.preventDefault();

    if (filename === '') {
      alert("Filename can't be blank!");
      return;
    }

    // Check for existing filename
    if (allFilenames.includes(filename) && filename !== doc.filename) {
      alert("Filename already exists; please choose a different name!");
      return;
    }

    if ((doc.category || "") !== category && doc.hasLinkedDataElement) {
      if (!window.confirm("Changing the category of this document will unlink it from its associated data element(s). Are you sure you want to proceed?")) {
        return;
      }
    }

    if (doc.filename === filename && (doc.category || "") === category && (doc.effectiveDate || null) === (effectiveDate || null) && doc.expires === expires && (doc.notes || "") === notes && (doc.archived === true) === archived) {
      onClose();
      return;
    }

    setSaving(true);
    authenticatedFetch({
      method: "PUT",
      requestData: {fromFilename: doc.filename, toFilename: filename, category: category, effectiveDate: effectiveDate, expiration: expires, notes: notes, archived: archived},
      path: api.path("file"),
      additionalHeaders: api.headers,
      successHandler: function(result) {
        setSaving(false);
        onSaved();
        setDocumentsRefresh(documentsRefresh + 1);
        onClose();

        const msg = (result && typeof result.message === 'string' && result.message !== 'success')
          ? result.message
          : "Updated!";

        notyf.open({type: "success", message: msg, duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
      },
      errorHandler: function() {
        setSaving(false);
        alert("Something went wrong; please try again.");
      }
    });
  }

  return (
    // No backdrop and a high z-index so this also works on top of DocumentManagementModal
    <Offcanvas show={doc != null} onHide={onClose} onEntered={() => { if (filenameRef.current) { filenameRef.current.focus(); filenameRef.current.select(); } }} placement="end" backdrop={false} style={{zIndex: 1100, width: "400px", maxWidth: "100vw", boxShadow: "-4px 0 24px rgba(0,0,0,0.15)"}}>
      <Offcanvas.Header closeButton>
        <Offcanvas.Title style={{fontSize: "1.1em"}}>Edit document</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <Form onSubmit={save}>
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control ref={filenameRef} value={filename} onChange={(e) => setFilename(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Category</Form.Label>
            <Form.Select value={category} onChange={(e) => setCategory(e.target.value)}>
              {rawDocumentCategories.map((c, i) => <option key={i} value={c.value}>{c.label}</option>)}
            </Form.Select>
            {doc && doc.hasLinkedDataElement &&
              <Form.Text className="text-muted">Linked to a data element; changing the category will unlink it.</Form.Text>}
          </Form.Group>
          <div className="d-flex gap-3 mb-3">
            <Form.Group style={{flex: 1}}>
              <Form.Label>Effective</Form.Label>
              <EditableExpires warnWithColors={false} localExpires={effectiveDate} localActive={true} editingExpires={true} handleExpiresChanged={(d) => setEffectiveDate(blankToNull(d))} size="md" width="100%" />
            </Form.Group>
            <Form.Group style={{flex: 1}}>
              <Form.Label>Expires</Form.Label>
              <EditableExpires warnWithColors={false} localExpires={expires} localActive={true} editingExpires={true} handleExpiresChanged={(d) => setExpires(blankToNull(d))} size="md" width="100%" />
            </Form.Group>
          </div>
          {showNotes &&
            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control as="textarea" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Form.Group>}
          <Form.Check type="checkbox" id="document-archived" className="mb-3" label="Archived" checked={archived} onChange={(e) => setArchived(e.target.checked)} />
          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? "Saving..." : "Save"}</Button>
          </div>
        </Form>
      </Offcanvas.Body>
    </Offcanvas>
  );
}

const DocumentRow = ({ doc, api, canEdit, showNotes, onEdit, refreshFileList }) => {
  const notyf = useContext(NotyfContext);
  const { documentsRefresh, setDocumentsRefresh } = useContext(UserSettingsContext);

  function handleDownload(e) {
    e.preventDefault();
    authenticatedFetch({
      method: "POST",
      requestData: {filename: doc.filename},
      path: api.path("file/signeddownloadurl"),
      additionalHeaders: api.headers,
      successHandler: function(result) {
        window.open(result.url);
      },
      errorHandler: function() {
        alert("Something went wrong; please try again.")
      }
    });
  }

  function handleToggleArchived() {
    const archiving = !doc.archived;
    if (archiving && !window.confirm("Are you sure you want to archive: " + doc.filename + "? Archived documents are hidden unless \"Show outdated\" is checked.")) {
      return;
    }
    authenticatedFetch({
      method: "PUT",
      requestData: {fromFilename: doc.filename, toFilename: doc.filename, category: doc.category || "", effectiveDate: doc.effectiveDate, expiration: doc.expires, notes: doc.notes || "", archived: archiving},
      path: api.path("file"),
      additionalHeaders: api.headers,
      successHandler: function() {
        refreshFileList();
        setDocumentsRefresh(documentsRefresh + 1);
        notyf.open({type: "success", message: archiving ? "Archived!" : "Unarchived!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
      },
      errorHandler: function() {
        alert("Something went wrong; please try again.")
      }
    });
  }

  function handleDelete() {
    const msg = doc.hasLinkedDataElement
      ? "Deleting this document will also unlink it from its associated data element(s). Are you sure you want to delete: " + doc.filename + "?"
      : "Are you sure you want to delete: " + doc.filename + "?";

    if (window.confirm(msg)) {
      authenticatedFetch({
        method: "POST",
        requestData: {filename: doc.filename},
        path: api.path("file/delete"),
        additionalHeaders: api.headers,
        successHandler: function() {
          refreshFileList();
          setDocumentsRefresh(documentsRefresh + 1);
          notyf.open({type: "success", message: "Deleted!", duration: 5000, dismissable: true, ripple: true, background: '#3AAFA9'});
        },
        errorHandler: function() {
          alert("Something went wrong; please try again.")
        }
      });
    }
  }

  let name = displayFilename(doc.filename);
  if (name.length > 100) {
    name = name.substring(0, 98) + "...";
  }

  return (
    <tr style={doc.archived ? {opacity: 0.6} : undefined}>
      <td style={{verticalAlign: "middle", padding: 0}}>
        <OverlayTrigger placement="bottom" overlay={<Tooltip>{doc.filename}</Tooltip>}>
          <div role="button" tabIndex={0} onClick={handleDownload} onKeyDown={(e) => { if (e.key === "Enter") handleDownload(e); }} style={{cursor: "pointer", padding: "0.25rem", height: "100%"}}>
            <div style={{wordBreak: "break-word"}}>
              <Download style={{color: '#3AAFA9', height: '12px', width: '12px'}} />&nbsp;{name}
            </div>
            {doc.linkedDataElementExtra && doc.linkedDataElementExtra.length > 0 &&
              <span style={{display: "inline-block", maxWidth: "100%", marginTop: "2px", verticalAlign: "top", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "0.78em", fontWeight: 400, color: "#6c757d", backgroundColor: "transparent", border: "1px solid #e3e6e9", borderRadius: "4px", padding: "0 6px"}}>
                {doc.linkedDataElementExtra}
              </span>}
          </div>
        </OverlayTrigger>
      </td>
      <td style={{verticalAlign: "middle", whiteSpace: "nowrap"}}>{doc.effectiveDate ? formatDate(doc.effectiveDate) : <span className="text-muted">&mdash;</span>}</td>
      <td style={{verticalAlign: "middle"}}><ExpiryStatus expires={doc.expires} archived={doc.archived} /></td>
      {showNotes &&
        <td style={{verticalAlign: "middle", whiteSpace: "pre-line", wordBreak: "break-word"}}>{doc.notes}</td>}
      <td style={{verticalAlign: "middle", textAlign: "right", whiteSpace: "nowrap"}}>
        {canEdit &&
          <span className="d-inline-flex align-items-center gap-2 text-muted">
            <IconAction label="Edit" onClick={() => onEdit(doc)}><Edit2 style={{height: "14px", width: "14px"}} /></IconAction>
            <IconAction label={doc.archived ? "Unarchive" : "Archive"} onClick={handleToggleArchived}>
              {doc.archived ? <RotateCcw style={{height: "14px", width: "14px"}} /> : <Archive style={{height: "14px", width: "14px"}} />}
            </IconAction>
            <IconAction label="Delete" onClick={handleDelete}><Trash2 style={{height: "14px", width: "14px"}} /></IconAction>
          </span>}
      </td>
    </tr>
  );
}

const SortableDocumentTable = ({ entityId, entityType, refreshFileList, handleFilesAdded, uploading, uploadsCompleted, singleUploadedFilename, isLoaded, error, documentList, canEdit, isH3AdminFile, isRevenueTeamSpanningFile, isPracticeSpecificFile, practiceId }) => {
  const [dragOver, setDragOver] = useState(false);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showOutdated, setShowOutdated] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [editingDoc, setEditingDoc] = useState(null);
  const fileInputRef = useRef(null);

  // After a single-file upload, open the edit drawer for it once it shows up in the refreshed list
  const [pendingEditFilename, setPendingEditFilename] = useState(null);

  useEffect(() => {
    if (singleUploadedFilename && canEdit) {
      setPendingEditFilename(singleUploadedFilename.filename);
    }
  }, [singleUploadedFilename, canEdit]);

  useEffect(() => {
    if (pendingEditFilename) {
      const uploaded = documentList.find((d) => d.filename === pendingEditFilename);
      if (uploaded) {
        setEditingDoc(uploaded);
        setPendingEditFilename(null);
      }
    }
  }, [pendingEditFilename, documentList]);

  // New uploads land in Uncategorized, so open it once an upload completes
  useEffect(() => {
    if (uploadsCompleted > 0) {
      setExpanded((prev) => ({...prev, "": true}));
    }
  }, [uploadsCompleted]);

  const { isH3Admin, authorities } = useContext(UserSettingsContext);
  const rawDocumentCategories = getRawDocumentCategories(isH3Admin, authorities, (isH3AdminFile && entityType === "revenueteam"), ((isRevenueTeamSpanningFile || isPracticeSpecificFile) && entityType === "facility"), ((isRevenueTeamSpanningFile || isPracticeSpecificFile) && entityType === "carrier"));
  const categoryLabels = {};
  rawDocumentCategories.forEach((c) => { categoryLabels[c.value] = c.label; });

  const showNotes = !isRevenueTeamSpanningFile && !isPracticeSpecificFile;
  const api = buildApi(entityType, entityId, isH3AdminFile, isRevenueTeamSpanningFile, isPracticeSpecificFile, practiceId);

  const allFilenames = useMemo(() => documentList.map((row) => row.filename), [documentList]);

  // A selected/pending document belongs to the entity and file scope it was chosen in
  useEffect(() => {
    setEditingDoc(null);
    setPendingEditFilename(null);
  }, [entityId, entityType, isH3AdminFile, isRevenueTeamSpanningFile, isPracticeSpecificFile, practiceId]);

  // The editing drawer works off the latest data, so it picks up refreshes
  const liveEditingDoc = editingDoc ? (documentList.find((d) => d.filename === editingDoc.filename) || editingDoc) : null;

  const visibleDocs = useMemo(() => {
    return showOutdated ? documentList : documentList.filter((doc) => !isOutdated(doc));
  }, [documentList, showOutdated]);

  // Independent of the checkbox, so the option stays available once checked
  const outdatedCount = useMemo(() => documentList.filter(isOutdated).length, [documentList]);

  const groups = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = visibleDocs.filter((doc) => {
      if (categoryFilter !== "all" && (doc.category || "") !== categoryFilter) return false;
      if (term) {
        const haystack = [displayFilename(doc.filename), doc.notes, doc.linkedDataElementExtra, categoryLabels[doc.category || ""]].join(" ").toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });

    const byCategory = {};
    filtered.forEach((doc) => {
      const key = doc.category || "";
      (byCategory[key] = byCategory[key] || []).push(doc);
    });

    const rank = (key) => {
      if (key === "") return CATEGORY_PRIORITY.length + 1;
      const idx = CATEGORY_PRIORITY.indexOf(key);
      return idx === -1 ? CATEGORY_PRIORITY.length : idx;
    };

    return Object.keys(byCategory)
      .sort((a, b) => (rank(a) - rank(b)) || String(categoryLabels[a] || a).localeCompare(String(categoryLabels[b] || b)))
      .map((key) => ({
        key: key,
        label: key === "" ? "Uncategorized" : (categoryLabels[key] || key),
        // Soonest expiration first; documents without one go last
        docs: byCategory[key].sort((a, b) => {
          if (a.expires && b.expires) return a.expires.localeCompare(b.expires);
          if (a.expires) return -1;
          if (b.expires) return 1;
          return displayFilename(a.filename).localeCompare(displayFilename(b.filename));
        }),
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleDocs, search, categoryFilter, rawDocumentCategories.length]);

  const setAllGroupsOpen = (open) => {
    const next = {};
    groups.forEach((g) => { next[g.key] = open; });
    setExpanded(next);
  };

  const shownCount = groups.reduce((sum, g) => sum + g.docs.length, 0);
  // Groups start collapsed, but filtering/searching is a request to see the matches
  const forceOpen = search.trim() !== "" || categoryFilter !== "all";

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <Form.Control size="sm" style={{maxWidth: "260px"}} value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${visibleDocs.length} document(s)...`} />
        <Form.Select size="sm" style={{width: "auto"}} value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} aria-label="Filter by category">
          <option value="all">All categories</option>
          {rawDocumentCategories.filter((c) => c.value !== "").map((c, i) => <option key={i} value={c.value}>{c.label}</option>)}
          <option value="">(no category)</option>
        </Form.Select>
        {canEdit && isLoaded && !error &&
          <>
            <input type="file" multiple ref={fileInputRef} style={{display: "none"}} onChange={(e) => { handleFilesAdded(e); e.target.value = ""; }} />
            <Button size="sm" className="ms-auto" disabled={uploading} onClick={() => fileInputRef.current && fileInputRef.current.click()}>
              {uploading ? <Spinner animation="border" size="sm" /> : <><Upload style={{height: "14px", width: "14px", marginTop: "-2px"}} /> Upload</>}
            </Button>
          </>}
      </div>

      {error ? <div>Something went wrong; please reload the page...</div> :
       !isLoaded ? <div>Loading...</div> :
       groups.length === 0 ? <div className="text-muted py-3">{documentList.length === 0 ? "No documents yet." : "No documents match the current filters."}</div> :
       <Table hover size="sm" className="mb-3" style={{tableLayout: "fixed", borderTop: "1px solid #dee2e6"}}>
        <colgroup>
          {/* With notes, Notes takes the leftover space; without, Name does */}
          <col style={showNotes ? {width: "38%"} : undefined} />
          <col style={{width: "96px"}} />
          <col style={{width: "140px"}} />
          {showNotes && <col />}
          <col style={{width: "80px"}} />
        </colgroup>
        <thead>
          <tr className="text-muted" style={{fontSize: "0.8em"}}>
            <th style={thStyle}>Name</th>
            <th style={thStyle}>Effective</th>
            <th style={thStyle}>Expires</th>
            {showNotes && <th style={thStyle}>Notes</th>}
            <th style={{...thStyle, textAlign: "right", whiteSpace: "nowrap"}}>
              {groups.length > 1 &&
                <>
                  <button type="button" className="btn btn-link p-0 text-muted border-0 shadow-none" title="Expand all" aria-label="Expand all" onClick={() => setAllGroupsOpen(true)}>
                    <ChevronsIcon points="M4 6l4-4 4 4M4 10l4 4 4-4" />
                  </button>
                  <button type="button" className="btn btn-link p-0 ms-1 text-muted border-0 shadow-none" title="Collapse all" aria-label="Collapse all" onClick={() => setAllGroupsOpen(false)}>
                    <ChevronsIcon points="M4 2l4 4 4-4M4 14l4-4 4 4" />
                  </button>
                </>}
            </th>
          </tr>
        </thead>
        <tbody style={{borderTop: "none"}}>
          {groups.map((group) => {
            // The priority categories start open; everything else starts collapsed
            const isOpen = expanded[group.key] ?? CATEGORY_PRIORITY.includes(group.key);
            const isCollapsed = !isOpen && !forceOpen;
            return (
              <React.Fragment key={group.key}>
                <tr role="button" style={{userSelect: "none"}} onClick={() => setExpanded({...expanded, [group.key]: !isOpen})}>
                  <td colSpan={showNotes ? 5 : 4} style={{backgroundColor: "#e8f3f1", padding: "4px 8px", fontSize: "0.8em", fontWeight: 600, letterSpacing: "0.03em", textTransform: "uppercase", color: "#2b7a78"}}>
                    {isCollapsed ? <ChevronRight style={{height: "14px", width: "14px"}} /> : <ChevronDown style={{height: "14px", width: "14px"}} />}
                    &nbsp;{group.label} <span style={{fontWeight: 400, color: "#6b8f8c"}}>({group.docs.length})</span>
                  </td>
                </tr>
                {!isCollapsed && group.docs.map((doc) => (
                  <DocumentRow key={doc.filename} doc={doc} api={api} canEdit={canEdit} showNotes={showNotes} onEdit={setEditingDoc} refreshFileList={refreshFileList} />
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
       </Table>}

      {isLoaded && !error &&
        <div className="d-flex justify-content-between align-items-center mb-3 text-muted" style={{fontSize: "0.85em"}}>
          <span>Showing <b>{shownCount}</b> of {visibleDocs.length}</span>
          {outdatedCount > 0 &&
          <Form.Check type="checkbox" id="documents-show-outdated" className="mb-0 d-flex align-items-center gap-2 text-muted" style={{paddingLeft: 0}}
          title={"Include archived documents and those that expired more than " + OUTDATED_DAYS + " days ago"}>
          <Form.Check.Input type="checkbox" className="m-0" checked={showOutdated} onChange={(e) => setShowOutdated(e.target.checked)} />
          <Form.Check.Label>{"Show outdated" + (!showOutdated ? " (" + outdatedCount + ")" : "")}</Form.Check.Label>
        </Form.Check>}
        </div>}

      {!uploading && !error && isLoaded && canEdit &&
        <Form.Control
          type="file"
          size="sm"
          multiple
          onChange={(e) => { setDragOver(false); handleFilesAdded(e); }}
          onDragEnter={(e) => { if (e.dataTransfer.types.includes("Files")) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={() => setDragOver(false)}
          style={{
            padding: "1.5rem",
            height: "auto",
            border: dragOver ? "2px dashed #3AAFA9" : "2px dashed #adb5bd",
            backgroundColor: dragOver ? "#e3f4f3" : "",
            transition: "background-color 0.15s, border-color 0.15s"
          }}
        />}
      {uploading && <div style={{textAlign: "center"}}><Spinner animation="border" size="sm" /></div>}

      <DocumentEditDrawer doc={liveEditingDoc} allFilenames={allFilenames} api={api} showNotes={showNotes} rawDocumentCategories={rawDocumentCategories} onClose={() => setEditingDoc(null)} onSaved={refreshFileList} />
    </>
  );
}


const DocumentList = ({ entityId, entityType, canEdit, isH3AdminFile=false, isRevenueTeamSpanningFile=false, isPracticeSpecificFile=false, practiceId=0 }) => {
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [data, setData] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadsCompleted, setUploadsCompleted] = useState(0);
    const [singleUploadedFilename, setSingleUploadedFilename] = useState(null);
    const latestRefreshId = useRef(0);

    // Needing the practice ID for practice specific case has pushed this abstraction beyond its limit
  
    const { documentsRefresh, tasksRefresh } = useContext(UserSettingsContext);

    useEffect(() => {
      refreshFileList();
    }, [entityId, entityType, isRevenueTeamSpanningFile, isPracticeSpecificFile, practiceId, documentsRefresh, tasksRefresh]);
  
    function refreshFileList()
    {
      // Reload the file list; only the most recently issued request is allowed to update state
      const refreshId = ++latestRefreshId.current;
      let additionalHeaders = {};
      if (isPracticeSpecificFile)
      {
        additionalHeaders["practiceId"] = practiceId;
      }
      authenticatedFetch({
        path: entityType + "/" + (isH3AdminFile ? "h3admin/" : (isRevenueTeamSpanningFile ? "revenueteamspanning/": (isPracticeSpecificFile ? "practicespecific/" : ""))) + entityId + "/files",
        additionalHeaders: additionalHeaders,
        successHandler: function(result) {
          if (refreshId !== latestRefreshId.current) return;
          setIsLoaded(true);
          setData(result);
        },
        errorHandler: function(error) {
          if (refreshId !== latestRefreshId.current) return;
          setIsLoaded(true);
          setError(error);
        }
      });
    }

    function handleFilesAdded(e) {
      e.preventDefault();
      setUploading(true);
  
      if (e.target.files && e.target.files.length > 0)
      {
        var proceed = true;
        if (data && data.length > 0)
        {
          for (var j=0; j<e.target.files.length; j++)
          {
            for (var k=0; k < data.length; k++)
            {
              if (e.target.files[j].name === data[k].filename)
              {
                if (!window.confirm("Are you sure you want to overwrite the existing file: " + data[k].filename + "?"))
                {
                  proceed = false;
                  break;
                }
              }
            }
          }
        }
  
        if (!proceed)
        {
          setUploading(false);
          e.target.value = null;
          return;
        }
  
        const uploadedNames = Array.from(e.target.files).map((file) => file.name);
        const uploads = Array.from(e.target.files).map((file) => new Promise((resolve) => {
          const name = file.name;
          const contentType = file.type;

          let additionalHeaders = {};
          if (isPracticeSpecificFile)
          {
            additionalHeaders["practiceId"] = practiceId;
          }

          authenticatedFetch({
            method: "POST",
            requestData: {filename: name, contentType: contentType},
            path: entityType + "/" + (isH3AdminFile ? "h3admin/" : (isRevenueTeamSpanningFile ? "revenueteamspanning/": (isPracticeSpecificFile ? "practicespecific/" : ""))) + entityId + "/file/signeduploadurl",
            additionalHeaders: additionalHeaders,
            successHandler: function(result) {
              // Got a signed S3 url, PUT to it
              var requestMetadata = {
                method: 'PUT',
                headers: {
                    'Content-Type': contentType
                },
                body: file
              };

              fetch(result.url, requestMetadata).then(res => {
                resolve(res.ok);
              }).catch(() => {
                resolve(false);
              });
            },
            errorHandler: function(error) {
              resolve(false);
            }
          });
        }));

        // Wait for every upload to settle, then refresh once so the listing includes all of them
        Promise.all(uploads).then((results) => {
          setUploading(false);
          if (results.includes(true))
          {
            setUploadsCompleted((count) => count + 1);
          }
          if (uploadedNames.length === 1 && results[0] === true)
          {
            // Wrapped in an object so uploading the same name twice still triggers
            setSingleUploadedFilename({filename: uploadedNames[0]});
          }
          if (results.includes(false))
          {
            alert("Something went wrong; please try again.");
          }
          refreshFileList();
        });
      }
      else
      {
        setUploading(false);
      }
    }
  
    var documentList = [];
    if (isLoaded)
    {
      documentList = data.map((file, i) => {
        return {key: file.filename, filename: file.filename, modified: file.modified, effectiveDate: file.effectiveDate, expires: file.expiration, size: file.size, category: file.category, reminderSent: file.secondEmailReminderSent != null ? file.secondEmailReminderSent : file.firstEmailReminderSent, notes: file.notes, hasLinkedDataElement: file.hasLinkedDataElement, linkedDataElementExtra: file.linkedDataElementExtra, archived: file.archived === true};
      }, this);
    }
  
    return (
      <SortableDocumentTable isH3AdminFile={isH3AdminFile} isRevenueTeamSpanningFile={isRevenueTeamSpanningFile} isPracticeSpecificFile={isPracticeSpecificFile} entityId={entityId} entityType={entityType} refreshFileList={refreshFileList} handleFilesAdded={handleFilesAdded} uploading={uploading} uploadsCompleted={uploadsCompleted} singleUploadedFilename={singleUploadedFilename} isLoaded={isLoaded} error={error} documentList={documentList} canEdit={canEdit} practiceId={practiceId} />
      );
  }

  export default DocumentList;
